import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import InsertLink from "@mui/icons-material/InsertLink";
import { IconButton, Tooltip } from "@mui/material";
import { COMMAND_PRIORITY_LOW, SELECTION_CHANGE_COMMAND } from "lexical";
import type { FC } from "react";
import { useEffect, useState } from "react";
import type { EditorModalState } from "../../lexical-context";
import { useLexicalContext } from "../../lexical-context";
import { $multipleElementsSelected } from "../../utils/multiple-elements-selected";
import { floatingToolbarStyles } from "./floating-toolbar-styles";
import { useLinkButtonOnClick } from "./hooks/use-link-button-on-click";
import type { ToolbarProps } from "./props";
import { ToolbarIconButton } from "./toolbar-icon-button";

export const LinkButton: FC<
  ToolbarProps & {
    handleOpen: (data: EditorModalState) => void;
    active: boolean;
  }
> = ({ handleOpen, disabled, mode, active }) => {
  const [forceDisabled, setForceDisabled] = useState(false);
  const [editor] = useLexicalComposerContext();
  const { format } = useLexicalContext();

  const linkButtonOnClick = useLinkButtonOnClick(editor, handleOpen);

  useEffect(
    () =>
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          setForceDisabled($multipleElementsSelected());

          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [editor],
  );

  // The SMS top-level toolbar shows a labelled "Add link" button; everywhere else (and the
  // floating edit toolbar) keeps the compact icon-only button.
  if (format === "sms" && mode === "toolbar") {
    return (
      <ToolbarIconButton
        data-analytics-id="sms-editor-add-link"
        disabled={disabled || forceDisabled}
        data-testid={`${mode}-button-link`}
        label="Add Link"
        onClick={linkButtonOnClick}
        icon={<InsertLink sx={floatingToolbarStyles[mode].formatIcon} />}
      />
    );
  }

  return (
    <>
      <Tooltip title="Link">
        <IconButton
          data-analytics-id={format === "sms" ? "sms-editor-add-link" : `lexical-${mode}-link`}
          disabled={disabled || forceDisabled}
          data-testid={`${mode}-button-link`}
          aria-label="Link"
          data-active={active}
          aria-pressed={active}
          tabIndex={-1}
          onClick={linkButtonOnClick}
          sx={{
            ...floatingToolbarStyles[mode].button,
            backgroundColor: active ? "background.active" : "background.neutral",
          }}
        >
          <InsertLink sx={floatingToolbarStyles[mode].formatIcon} />
        </IconButton>
      </Tooltip>
    </>
  );
};
